import React, { useState, useRef } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Switch,
    Box,
    Alert,
    CircularProgress,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Category } from "../../types";
import { productService } from "../../services/productService";

const schema = yup.object({
    name: yup.string().required("Category name is required"),
    description: yup.string().optional(),
    parent_id: yup.string().optional(),
    display_order: yup.number().min(0, "Must be 0 or greater").optional(),
    image_url: yup.string().url("Must be a valid URL").optional().nullable(),
    is_active: yup.boolean().optional(),
    is_featured: yup.boolean().optional(),
});

interface CreateCategoryFormData {
    name: string;
    description?: string;
    parent_id?: string;
    display_order?: number;
    image_url?: string;
    is_active: boolean;
    is_featured: boolean;
}

interface CreateCategoryModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: (category: Category) => void;
    parentCategories?: Category[];
}

export const CreateCategoryModal: React.FC<CreateCategoryModalProps> = ({
    open,
    onClose,
    onSuccess,
    parentCategories = [],
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>("");
    const nameInputRef = useRef<HTMLInputElement | null>(null);

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<CreateCategoryFormData>({
        resolver: yupResolver(schema) as any,
        defaultValues: {
            name: "",
            description: "",
            parent_id: "",
            display_order: 0,
            image_url: "",
            is_active: true,
            is_featured: false,
        },
    });

    const handleFormSubmit = async (data: CreateCategoryFormData) => {
        try {
            setLoading(true);
            setError("");

            const categoryData = {
                name: data.name,
                description: data.description || undefined,
                parent_id: data.parent_id || undefined,
                display_order: data.display_order ?? 0,
                image_url: data.image_url || undefined,
                is_active: data.is_active,
                is_featured: data.is_featured ?? false,
            };

            const newCategory =
                await productService.createCategory(categoryData);
            onSuccess(newCategory);
            handleClose();
        } catch (err: any) {
            setError(err.response?.data?.detail || "Failed to create category");
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        reset();
        setError("");
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            disableRestoreFocus
            TransitionProps={{ onEntered: () => nameInputRef.current?.focus() }}
        >
            <DialogTitle>Create New Category</DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Box component="form" sx={{ mt: 1 }}>
                    <Controller
                        name="name"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                margin="normal"
                                required
                                fullWidth
                                label="Category Name"
                                inputRef={nameInputRef}
                                autoFocus
                                error={!!errors.name}
                                helperText={errors.name?.message}
                            />
                        )}
                    />

                    <Controller
                        name="description"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                margin="normal"
                                fullWidth
                                label="Description"
                                multiline
                                rows={3}
                                error={!!errors.description}
                                helperText={errors.description?.message}
                            />
                        )}
                    />

                    {parentCategories.length > 0 && (
                        <Controller
                            name="parent_id"
                            control={control}
                            render={({ field }) => (
                                <FormControl
                                    fullWidth
                                    margin="normal"
                                    error={!!errors.parent_id}
                                >
                                    <InputLabel>
                                        Parent Category (Optional)
                                    </InputLabel>
                                    <Select
                                        {...field}
                                        label="Parent Category (Optional)"
                                    >
                                        <MenuItem value="">
                                            <em>None</em>
                                        </MenuItem>
                                        {parentCategories.map((category) => (
                                            <MenuItem
                                                key={category.category_id}
                                                value={category.category_id}
                                            >
                                                {category.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {errors.parent_id && (
                                        <Box
                                            sx={{
                                                color: "error.main",
                                                fontSize: "0.75rem",
                                                mt: 0.5,
                                            }}
                                        >
                                            {errors.parent_id.message}
                                        </Box>
                                    )}
                                </FormControl>
                            )}
                        />
                    )}

                    <Controller
                        name="display_order"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                margin="normal"
                                fullWidth
                                label="Display Order"
                                type="number"
                                inputProps={{ min: 0 }}
                                helperText="Lower numbers appear first"
                            />
                        )}
                    />

                    <Controller
                        name="image_url"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                margin="normal"
                                fullWidth
                                label="Image URL (Optional)"
                            />
                        )}
                    />

                    <Box sx={{ display: "flex", gap: 3, mt: 2 }}>
                        <Controller
                            name="is_active"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            {...field}
                                            checked={field.value}
                                        />
                                    }
                                    label="Active"
                                />
                            )}
                        />
                        <Controller
                            name="is_featured"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            {...field}
                                            checked={field.value}
                                            color="warning"
                                        />
                                    }
                                    label="Featured"
                                />
                            )}
                        />
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit(handleFormSubmit)}
                    variant="contained"
                    disabled={loading}
                >
                    {loading ? (
                        <CircularProgress size={24} />
                    ) : (
                        "Create Category"
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
